import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import "./AuthModal.css";
import Loader from "./Loader";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AuthModal({ isOpen, onClose, onLoginSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // "login", "register", "forgot", "verify", "reset"

  const [formData, setFormData] = useState({
    // shared
    email: "",
    password: "",
    // register-only
    confirmPassword: "",
    fname: "",
    lname: "",
    username: "",
    gender: "male",
    birthdate: "", // YYYY-MM-DD
    // verification code
    verificationCode: "",
    // reset password
    resetPassword: "",
    resetPasswordConfirmation: "",
  });

  // Store reset email for password reset flow
  const [resetData, setResetData] = useState({
    email: "",
  });

  // Countdown timer for verification code expiration
  const [countdown, setCountdown] = useState(0);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: "",
        password: "",
        confirmPassword: "",
        fname: "",
        lname: "",
        username: "",
        gender: "male",
        birthdate: "",
        verificationCode: "",
        resetPassword: "",
        resetPasswordConfirmation: "",
      });
      setAuthMode("login");
      setResetData({ email: "" });
      setCountdown(0);
    }
  }, [isOpen]);

  // Countdown timer effect
  useEffect(() => {
    let interval = null;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(countdown => countdown - 1);
      }, 1000);
    } else if (countdown === 0 && authMode === "verify") {
      // Code expired, show message
      toast.error("Verification code has expired. Please request a new one.");
    }
    return () => clearInterval(interval);
  }, [countdown, authMode]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Format countdown time
  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // -----------------------------
  // LOGIN
  // -----------------------------
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Login failed");

      // Token handling (supporting multiple key names)
      const token = data.token || data.access_token || data.auth_token;
      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("access_token", token);
      }
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        // Save user role separately for easy access
        if (data.user.role_id) {
          localStorage.setItem("user_role", data.user.role_id);
        }
      }

      toast.success("Login successful!");
      
      // Redirect admin users to admin dashboard
      if (data.user && data.user.role_id === "1") {
        window.location.href = "/admin";
      } else {
        onLoginSuccess?.();
        onClose?.();
      }
    } catch (err) {
      toast.error(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // FORGOT PASSWORD
  // -----------------------------
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Store email for verification step
        setResetData({
          email: data.email || formData.email,
        });
        
        toast.success("Verification code sent! Check your email.");
        setAuthMode("verify");
        setCountdown(600); // 10 minutes = 600 seconds
      } else {
        // Handle validation errors
        if (data.errors && data.errors.email) {
          toast.error(data.errors.email[0]);
        } else if (data.message) {
          toast.error(data.message);
        } else {
          toast.error("Failed to send verification code. Please try again.");
        }
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // VERIFY CODE
  // -----------------------------
  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!formData.verificationCode) {
      toast.error("Please enter the verification code");
      return;
    }

    if (!/^\d{6}$/.test(formData.verificationCode)) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/verify-reset-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: resetData.email,
          verification_code: formData.verificationCode,
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Code verified! You can now set your new password.");
        setAuthMode("reset");
      } else {
        // Handle specific error cases
        if (data.message && data.message.includes("expired")) {
          toast.error("Verification code has expired. Please request a new one.");
          setAuthMode("forgot");
          setCountdown(0);
        } else if (data.message && data.message.includes("Invalid")) {
          toast.error("Invalid verification code. Please check and try again.");
        } else {
          toast.error(data.message || "Invalid verification code");
        }
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // RESET PASSWORD
  // -----------------------------
  const handleResetPassword = async (e) => {
    e.preventDefault();

    const { resetPassword, resetPasswordConfirmation, verificationCode } = formData;

    if (!resetPassword || !resetPasswordConfirmation || !verificationCode) {
      toast.error("Please fill in all fields");
      return;
    }

    if (resetPassword !== resetPasswordConfirmation) {
      toast.error("Passwords do not match");
      return;
    }

    if (resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: resetData.email,
          verification_code: verificationCode,
          password: resetPassword,
          password_confirmation: resetPasswordConfirmation,
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Password reset successfully! You can now login.");
        setAuthMode("login");
        setResetData({ email: "" });
        // Clear reset password fields
        setFormData(prev => ({
          ...prev,
          verificationCode: "",
          resetPassword: "",
          resetPasswordConfirmation: "",
        }));
      } else {
        // Handle specific error cases
        if (data.message && data.message.includes("expired")) {
          toast.error("Verification code has expired. Please request a new one.");
          setAuthMode("forgot");
          setCountdown(0);
        } else if (data.message && data.message.includes("Invalid")) {
          toast.error("Invalid verification code. Please check and try again.");
        } else {
          toast.error(data.message || "Failed to reset password");
        }
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // REGISTER (exact body you provided)
  // -----------------------------
  const handleRegister = async (e) => {
    e.preventDefault();

    // Basic checks
    const {
      fname,
      lname,
      username,
      email,
      password,
      confirmPassword,
      gender,
      birthdate,
    } = formData;

    if (
      !fname ||
      !lname ||
      !username ||
      !email ||
      !password ||
      !confirmPassword ||
      !gender ||
      !birthdate
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    // birthdate must be YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      toast.error("Birthdate must be in YYYY-MM-DD format");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fname,
        lname,
        username,
        email,
        password,
        password_confirmation: confirmPassword,
        gender,
        birthdate, // e.g., "1999-08-19"
      };

      const response = await fetch(`${API_BASE}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Registration failed");

      // If API returns token + user, store them
      const token = data.token || data.access_token || data.auth_token;
      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("access_token", token);
      }
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        // Save user role separately for easy access
        if (data.user.role_id) {
          localStorage.setItem("user_role", data.user.role_id);
        }
      }

      toast.success("Registration successful!");
      
      // Redirect admin users to admin dashboard
      if (data.user && data.user.role_id === "1") {
        window.location.href = "/admin";
      } else {
        onLoginSuccess?.();
        onClose?.();
      }
    } catch (err) {
      toast.error(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="auth-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="auth-modal-header">
          <h2>
            {authMode === "login" && "Sign In"}
            {authMode === "register" && "Create Account"}
            {authMode === "forgot" && "Forgot Password"}
            {authMode === "verify" && "Verify Code"}
            {authMode === "reset" && "Reset Password"}
          </h2>
          <p>
            {authMode === "login" && "Please sign in to add items to your cart"}
            {authMode === "register" && "Join us to start shopping and save your favorites"}
            {authMode === "forgot" && "Enter your email to receive a verification code"}
            {authMode === "verify" && "Enter the 6-digit code sent to your email"}
            {authMode === "reset" && "Enter your new password"}
          </p>
        </div>

        {authMode !== "forgot" && authMode !== "verify" && authMode !== "reset" && (
        <div className="auth-modal-tabs">
          <button
              className={`auth-tab ${authMode === "login" ? "active" : ""}`}
              onClick={() => setAuthMode("login")}
          >
            Sign In
          </button>
          <button
              className={`auth-tab ${authMode === "register" ? "active" : ""}`}
              onClick={() => setAuthMode("register")}
          >
            Sign Up
          </button>
        </div>
        )}

        <form
          onSubmit={
            authMode === "login" ? handleLogin :
            authMode === "register" ? handleRegister :
            authMode === "forgot" ? handleForgotPassword :
            authMode === "verify" ? handleVerifyCode :
            authMode === "reset" ? handleResetPassword :
            handleLogin
          }
          className="auth-form"
        >
          {authMode === "login" && (
            <>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <div className="form-group forgot-password-link">
                <button
                  type="button"
                  className="forgot-password-btn"
                  onClick={() => setAuthMode("forgot")}
                  disabled={loading}
                >
                  Forgot your password?
                </button>
              </div>
            </>
          )}

          {authMode === "forgot" && (
            <>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </>
          )}

          {authMode === "verify" && (
            <>
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  value={formData.verificationCode}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit code"
                  disabled={loading}
                  maxLength="6"
                  className="verification-code-input"
                />
                <div className="verification-code-hint">
                  Enter the 6-digit code sent to {resetData.email}
                  {countdown > 0 && (
                    <div className="countdown-timer">
                      Code expires in: {formatCountdown(countdown)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {authMode === "reset" && (
            <>
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  value={formData.verificationCode}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit code"
                  disabled={loading}
                  maxLength="6"
                  className="verification-code-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="resetPassword">New Password</label>
                <input
                  id="resetPassword"
                  name="resetPassword"
                  type="password"
                  value={formData.resetPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password (min 6 chars)"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="resetPasswordConfirmation">Confirm New Password</label>
                <input
                  id="resetPasswordConfirmation"
                  name="resetPasswordConfirmation"
                  type="password"
                  value={formData.resetPasswordConfirmation}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </>
          )}

          {authMode === "register" && (
            <>
              {/* Names in two columns */}
              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor="fname">First Name</label>
                  <input
                    id="fname"
                    name="fname"
                    type="text"
                    value={formData.fname}
                    onChange={handleInputChange}
                    placeholder="Your first name"
                    disabled={loading}
                    autoComplete="given-name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lname">Last Name</label>
                  <input
                    id="lname"
                    name="lname"
                    type="text"
                    value={formData.lname}
                    onChange={handleInputChange}
                    placeholder="Your last name"
                    disabled={loading}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                  disabled={loading}
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a password (min 6 chars)"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor="gender">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="birthdate">Birthdate</label>
                  <input
                    id="birthdate"
                    name="birthdate"
                    type="date"
                    value={formData.birthdate}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </>
          )}

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? (
              <Loader size={20} label="Loading" />
            ) : (
              authMode === "login" ? "Sign In" :
              authMode === "register" ? "Create Account" :
              authMode === "forgot" ? "Send Verification Code" :
              authMode === "verify" ? "Verify Code" :
              authMode === "reset" ? "Reset Password" :
              "Sign In"
            )}
          </button>
        </form>

        <div className="auth-modal-footer">
          {authMode === "login" && (
            <p>
              Don't have an account?{" "}
              <button
                type="button"
                className="auth-switch-btn"
                onClick={() => setAuthMode("register")}
                disabled={loading}
              >
                Sign up
              </button>
            </p>
          )}

          {authMode === "register" && (
            <p>
              Already have an account?{" "}
              <button
                type="button"
                className="auth-switch-btn"
                onClick={() => setAuthMode("login")}
                disabled={loading}
              >
                Sign in
              </button>
            </p>
          )}

          {authMode === "forgot" && (
            <p>
              Remember your password?{" "}
              <button
                type="button"
                className="auth-switch-btn"
                onClick={() => setAuthMode("login")}
                disabled={loading}
              >
                Sign in
              </button>
            </p>
          )}

          {authMode === "verify" && (
            <p>
              Didn't receive the code?{" "}
              <button
                type="button"
                className="auth-switch-btn"
                onClick={() => {
                  setAuthMode("forgot");
                  setCountdown(0);
                }}
                disabled={loading}
              >
                Resend code
              </button>
            </p>
          )}

          {authMode === "reset" && (
            <p>
              Remember your password?{" "}
            <button
              type="button"
              className="auth-switch-btn"
                onClick={() => setAuthMode("login")}
              disabled={loading}
            >
                Sign in
            </button>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
